import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Search, Check, ChevronsUpDown, X, Plus } from 'lucide-react';
import { DatePicker } from '@/components/ui/date-picker';
import { format } from 'date-fns';
import { businessDay, formatBusinessDate, formatCurrency } from '@/lib/format';
import { toast } from 'sonner';

interface Transaction {
    id: number;
    amount: number;
    transaction_at: string;
    description: string | null;
    type: string;
    /** The pool holds GHS and USD lines; each amount is printed in its own currency. */
    currency: string;
}

interface TransactionSelectorProps {
    value: number | null;
    onChange: (transactionId: number | null, transaction?: Transaction) => void;
    disabled?: boolean;
    placeholder?: string;
    /** Default type for new transactions created from this context */
    defaultType?: 'CREDIT' | 'DEBIT';
}

export default function TransactionSelector({
    value,
    onChange,
    disabled = false,
    placeholder = 'Select a transaction...',
    defaultType = 'CREDIT',
}: TransactionSelectorProps) {
    const queryClient = useQueryClient();
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateSheet, setShowCreateSheet] = useState(false);

    // New transaction form state
    const [newAmount, setNewAmount] = useState('');
    const [newDate, setNewDate] = useState('');
    const [newDescription, setNewDescription] = useState('');

    // Fetch unassigned transactions filtered by type
    const { data: unassignedTransactions, isLoading: isLoadingUnassigned } = useQuery({
        queryKey: ['unassigned-transactions', searchQuery, defaultType],
        queryFn: async () => {
            const { data, error } = await supabase.rpc('get_unassigned_transactions', {
                search_query: searchQuery || null,
                limit_count: 50,
                type_filter: defaultType,
            });
            if (error) throw error;
            return (data || []) as Transaction[];
        },
        staleTime: 30000,
    });

    // Fetch the currently selected transaction (if editing and it's not in unassigned list)
    const { data: currentTransaction, isLoading: isLoadingCurrent } = useQuery({
        queryKey: ['transaction', value],
        queryFn: async () => {
            if (!value) return null;
            const { data, error } = await supabase
                .from('transactions')
                .select('id, amount, transaction_at, description, type, currency')
                .eq('id', value)
                .single();
            if (error) throw error;
            return data as Transaction;
        },
        enabled: !!value,
        staleTime: 60000,
    });

    // Create transaction mutation
    const createTransactionMutation = useMutation({
        mutationFn: async () => {
            if (!newAmount || !newDate) throw new Error('Amount and date are required');

            const { data, error } = await supabase
                .from('transactions')
                .insert({
                    amount: parseFloat(newAmount),
                    transaction_at: newDate,
                    description: newDescription.trim() || null,
                    type: defaultType,
                })
                .select('id, amount, transaction_at, description, type, currency')
                .single();

            if (error) throw error;
            return data as Transaction;
        },
        onSuccess: (transaction) => {
            toast.success('Transaction created');
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions'] });
            // Auto-select the newly created transaction
            onChange(transaction.id, transaction);
            // Reset form and close sheet
            setNewAmount('');
            setNewDate('');
            setNewDescription('');
            setShowCreateSheet(false);
        },
        onError: (e: Error) => toast.error(e.message),
    });

    // Merge unassigned transactions with current transaction (if not already in list)
    const transactions = useMemo(() => {
        const list = [...(unassignedTransactions || [])];
        if (currentTransaction && !list.find(t => t.id === currentTransaction.id)) {
            list.unshift(currentTransaction);
        }
        return list;
    }, [unassignedTransactions, currentTransaction]);

    const isLoading = isLoadingUnassigned || (!!value && isLoadingCurrent);

    // Find the selected transaction for display
    const selectedTransaction = transactions?.find((t) => t.id === value) || currentTransaction;

    // In the row's own currency (GH₵250.00, US$3,000.00): the RPC returns it, and a USD payment
    // printed as cedis is a different amount of money.
    const formatAmount = (t: Transaction) => formatCurrency(t.amount, t.currency || 'GHS');

    const formatTransactionLabel = (t: Transaction) => {
        const date = formatBusinessDate(t.transaction_at);
        const amount = formatAmount(t);
        const desc = t.description ? ` - ${t.description}` : '';
        return `${date} | ${amount}${desc}`;
    };

    const handleOpenCreateSheet = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        setOpen(false);
        setShowCreateSheet(true);
    };

    const TransactionItem = ({ t, isSelected }: { t: Transaction; isSelected?: boolean }) => (
        <button
            type="button"
            className={`w-full text-left cursor-pointer group rounded-lg border p-3 transition-all hover:border-primary hover:bg-primary/5 ${isSelected
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'border-border/60 bg-card'
                }`}
            onClick={() => {
                onChange(t.id, t);
                setOpen(false);
            }}
        >
            <div className="flex justify-between items-start mb-2">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${t.type === 'DEBIT'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    }`}>
                    {format(businessDay(t.transaction_at)!, 'MMM d')}
                </span>
                {isSelected && <Check className="h-3.5 w-3.5 text-primary" />}
            </div>

            <div className="mb-1">
                <span className={`text-lg font-bold tracking-tight ${t.type === 'DEBIT' ? 'text-foreground' : 'text-green-600 dark:text-green-400'
                    }`}>
                    {formatAmount(t)}
                </span>
            </div>

            <div className="space-y-1">
                <p className="text-xs text-muted-foreground break-all">
                    {t.description || "No description"}
                </p>
                <code className="text-[10px] text-muted-foreground/50 font-mono">
                    #{t.id}
                </code>
            </div>
        </button>
    );

    // Filter out selected from list
    const otherTransactions = transactions.filter(t => t.id !== value);

    return (
        <>
            {/* Transaction Selection Sheet */}
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 h-[calc(100vh-2rem)] !w-[500px] !max-w-none rounded-2xl p-0 flex flex-col gap-0 border shadow-2xl bg-background data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right duration-300 focus:outline-none"
                >
                    <SheetHeader className="p-4 border-b">
                        <SheetTitle>Select Transaction</SheetTitle>
                    </SheetHeader>

                    {/* Search input */}
                    <div className="p-4 border-b bg-muted/10">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search transactions..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 bg-background"
                            />
                        </div>
                    </div>

                    <ScrollArea className="flex-1">
                        <div className="p-4 space-y-4">
                            {/* Clear selection button */}
                            {selectedTransaction && (
                                <button
                                    type="button"
                                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-accent cursor-pointer text-muted-foreground rounded-md transition-colors"
                                    onClick={() => {
                                        onChange(null);
                                        setOpen(false);
                                    }}
                                >
                                    <X className="h-4 w-4 shrink-0" />
                                    <span className="text-sm">Clear selection</span>
                                </button>
                            )}

                            {/* Selected item sticky at top */}
                            {selectedTransaction && (
                                <div className="space-y-2">
                                    <Label className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Currently Selected</Label>
                                    <TransactionItem t={selectedTransaction} isSelected />
                                </div>
                            )}

                            {/* Available Transactions */}
                            <div className="space-y-2">
                                <Label className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Available Transactions</Label>
                                {isLoadingUnassigned ? (
                                    <div className="flex flex-col gap-3">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="h-24 rounded-lg bg-muted/40 animate-pulse border border-border/40" />
                                        ))}
                                    </div>
                                ) : !otherTransactions?.length ? (
                                    <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed rounded-lg bg-muted/10">
                                        <div className="p-3 rounded-full bg-muted/20 mb-3">
                                            <Search className="h-6 w-6 text-muted-foreground" />
                                        </div>
                                        <p className="text-sm font-medium">No transactions found</p>
                                        <p className="text-xs text-muted-foreground mt-1 max-w-[200px]">
                                            {searchQuery ? "Try checking your spelling or using different keywords." : "No unassigned transactions available for selection."}
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {otherTransactions.map((t) => (
                                            <TransactionItem key={t.id} t={t} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </ScrollArea>

                    {/* Add new transaction button - fixed at bottom */}
                    <div className="p-4 border-t bg-muted/10">
                        <Button
                            className="w-full gap-2"
                            onClick={handleOpenCreateSheet}
                        >
                            <Plus className="h-4 w-4" />
                            Create New Transaction
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>

            {/* Trigger Button (outside Sheet) */}
            <button
                type="button"
                onClick={() => setOpen(true)}
                disabled={disabled || isLoading}
                className={`w-full text-left cursor-pointer rounded-lg border p-3 transition-all hover:border-primary hover:bg-primary/5 disabled:opacity-50 disabled:cursor-not-allowed ${selectedTransaction
                    ? 'border-primary/50 bg-primary/5'
                    : 'border-border/60 bg-card'
                    }`}
            >
                {isLoading ? (
                    <div className="flex items-center gap-2 py-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-muted-foreground">Loading...</span>
                    </div>
                ) : selectedTransaction ? (
                    <>
                        <div className="flex justify-between items-start mb-2">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${selectedTransaction.type === 'DEBIT'
                                ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                }`}>
                                {format(businessDay(selectedTransaction.transaction_at)!, 'MMM d')}
                            </span>
                            <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="mb-1">
                            <span className={`text-lg font-bold tracking-tight ${selectedTransaction.type === 'DEBIT' ? 'text-foreground' : 'text-green-600 dark:text-green-400'
                                }`}>
                                {formatAmount(selectedTransaction)}
                            </span>
                        </div>
                        <div className="space-y-1">
                            <p className="text-xs text-muted-foreground break-all">
                                {selectedTransaction.description || "No description"}
                            </p>
                            <code className="text-[10px] text-muted-foreground/50 font-mono">
                                #{selectedTransaction.id}
                            </code>
                        </div>
                    </>
                ) : (
                    <div className="flex items-center justify-between py-2">
                        <span className="text-sm text-muted-foreground">{placeholder}</span>
                        <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                    </div>
                )}
            </button>

            {/* Create Transaction Sheet */}
            <Sheet open={showCreateSheet} onOpenChange={setShowCreateSheet}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !w-[30vw] !max-w-none rounded-2xl flex flex-col shadow-2xl border bg-background"
                >
                    <SheetHeader>
                        <SheetTitle>Create New Transaction</SheetTitle>
                    </SheetHeader>

                    <div className="flex-1 mt-6 space-y-4">
                        <div className="space-y-2">
                            <Label>Amount (GHS) *</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={newAmount}
                                onChange={(e) => setNewAmount(e.target.value)}
                                placeholder="0.00"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Date *</Label>
                            <DatePicker
                                date={newDate ? new Date(newDate) : undefined}
                                setDate={(date) => setNewDate(date ? format(date, 'yyyy-MM-dd') : '')}
                            />
                        </div>



                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Input
                                value={newDescription}
                                onChange={(e) => setNewDescription(e.target.value)}
                                placeholder="Optional description"
                            />
                        </div>
                    </div>

                    <div className="pt-4 border-t flex justify-end gap-2">
                        <Button variant="outline" onClick={() => setShowCreateSheet(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={() => createTransactionMutation.mutate()}
                            disabled={!newAmount || !newDate || createTransactionMutation.isPending}
                        >
                            {createTransactionMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Create Transaction
                        </Button>
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}
